import { c as _c } from "react/compiler-runtime";
import { Button } from '@/components/Atoms/Button/Button';
import { Loading } from '@/components/Atoms/Loading/Loading';
import { Notification } from '@/components/Atoms/Notification/Notification';
import type { IHeadMetaProps } from '@/components/Molecules/HeadMeta/HeadMeta';
import { HeadMeta } from '@/components/Molecules/HeadMeta/HeadMeta';
import { ConfirmDetailsPopup } from '@/components/Organisms/ConfirmDetailsPopup/ConfirmDetailsPopup';
import { Footer } from '@/components/Organisms/Footer/Footer';
import { ToastNotificationContainer } from '@/components/Organisms/ToastNotification/ToastNotificationContainer';
import { RequireLogin } from '@/components/Pages/TransparentRedirectViews/RequireLogin';
import { SubscribeButtonLockContextProvider } from '@/contexts/AppContext/SubscribeButtonLockContext';
import { NavigationContextProvider, useNavigationContext } from '@/contexts/NavigationContext/NavigationContext';
import { NavigationWrapper } from '@/contexts/NavigationContext/NavigationWrapper';
import type { IAnalyticsConfiguration } from '@/services/Analytics/BBAnalytics';
import { prepareBBAnalytics, refreshBBAnalyticsProviders, trackPageLoad } from '@/services/Analytics/BBAnalytics';
import type { IBaseAnalyticsData } from '@/services/Analytics/bnefAnalyticsInterfaces';
import { breakpoints } from '@/styles/media';
import { externalSourceQuery, fromLoginQuery } from '@/utils/urls';
import type { NextComponentType } from 'next';
import Head from 'next/head';
import Router, { useRouter } from 'next/router';
import type { FC, ReactNode } from 'react';
import { Component, useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import type { ISessionData } from './AppContext';
import { AppContextProvider, useAppContext } from './AppContext';
export interface IBasePageProps {
  sessionData?: ISessionData;
  page?: {
    [key: string]: any;
    analyticsMetadata?: IBaseAnalyticsData;
  };
  analyticsConfiguration: IAnalyticsConfiguration;
  publicMetadata?: IHeadMetaProps;
  userAuthorizedToViewPage: boolean;
}
export class BaseAppContextContainer<T> extends Component<IBasePageProps & T, undefined> {
  public static getLayout(Component: NextComponentType, pageProps: any) {
    const {
      analyticsConfiguration,
      sessionData,
      page,
      publicMetadata,
      userAuthorizedToViewPage
    } = pageProps;
    if (!userAuthorizedToViewPage) {
      return <>
                    {publicMetadata && <HeadMeta {...publicMetadata} />}
                    <RequireLogin publicMetadata={publicMetadata} />
                </>;
    }
    const pageComponent = <>
                <Head>
                    <>
                        <meta name="theme-color" content="#0D9DDB" media="(prefers-color-scheme: light)" key="light" />
                        <meta name="theme-color" content="#0984B9" media="(prefers-color-scheme: dark)" key="dark" />
                    </>
                </Head>
                <AnalyticsContainer analyticsConfiguration={analyticsConfiguration} userAuthorizedToViewPage={userAuthorizedToViewPage} pageComponent={Component} page={page} />
            </>;
    return <AppContextProvider initialSession={sessionData}>
                <InnerApp pageComponent={pageComponent} />
            </AppContextProvider>;
  }
}
interface IAnalyticsContainer {
  analyticsConfiguration?: any;
  page?: any;
  pageComponent: NextComponentType;
  userAuthorizedToViewPage?: boolean;
}
class AnalyticsContainer extends Component<IAnalyticsContainer> {
  public componentDidMount(): void {
    const {
      analyticsConfiguration,
      page,
      userAuthorizedToViewPage
    } = this.props;
    if (userAuthorizedToViewPage) {
      prepareBBAnalytics(analyticsConfiguration, page?.analyticsMetadata ?? {});
    }
  }
  public componentDidUpdate(prevProps: any): void {
    if (this.props?.page?.analyticsMetadata === prevProps?.page?.analyticsMetadata) {
      return;
    }
    refreshBBAnalyticsProviders(this.props?.page?.analyticsMetadata);
  }
  public render() {
    const PageComponent = this.props.pageComponent;
    return <PageComponent {...this.props.page} />;
  }
}
const InnerApp = t0 => {
  const $ = _c(26);
  const {
    pageComponent
  } = t0;
  const {
    previewMode,
    navLinks,
    notificationBadge,
    announcement
  } = useAppContext();
  const router = useRouter();
  const [isLoadingRoute, setIsLoadingRoute] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  useEffect(_temp, t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  useEffect(_temp3, t2);
  let t3;
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      let refreshTimer;
      const handleRouteChange = url_0 => {
        setIsLoadingRoute(true);
        if (refreshTimer) {
          window.clearTimeout(refreshTimer);
        }
        refreshTimer = window.setTimeout(() => {
          if (window.location.pathname !== url_0) {
            console.warn("Client Side routing took too long. Performing full reload", {
              url: url_0
            });
            window.location.href = url_0;
          }
        }, 7500);
      };
      const handleRouteEnd = () => {
        window.clearTimeout(refreshTimer);
        setIsLoadingRoute(false);
      };
      Router.events.on("routeChangeStart", handleRouteChange);
      Router.events.on("routeChangeComplete", handleRouteEnd);
      return () => {
        window.clearTimeout(refreshTimer);
        Router.events.off("routeChangeStart", handleRouteChange);
        Router.events.off("routeChangeComplete", handleRouteEnd);
      };
    };
    t4 = [];
    $[2] = t3;
    $[3] = t4;
  } else {
    t3 = $[2];
    t4 = $[3];
  }
  useEffect(t3, t4);
  let announcementMessage;
  if (announcement?.path) {
    ;
    try {
      const pathRegex = new RegExp(announcement.path);
      if (pathRegex.test(router.asPath)) {
        announcementMessage = announcement.message;
      }
    } catch (t5) {
      const e = t5;
      console.error("Invalid announcement path regex:", announcement.path, e);
    }
  }
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <SkipLink kind="primary" href="#main">Skip to content</SkipLink>;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== announcementMessage) {
    t6 = announcementMessage && <Notification fullWidth={true} state="warning">{announcementMessage}</Notification>;
    $[5] = announcementMessage;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== pageComponent) {
    t7 = <MainContentWrap pageComponent={pageComponent} />;
    $[7] = pageComponent;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== t6 || $[10] !== t7) {
    t8 = <NavigationWrapper>{t6}{t7}</NavigationWrapper>;
    $[9] = t6;
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== navLinks || $[13] !== notificationBadge || $[14] !== t8) {
    t9 = <NavigationContextProvider navSections={navLinks} notificationBadge={notificationBadge}>{t8}</NavigationContextProvider>;
    $[12] = navLinks;
    $[13] = notificationBadge;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== isLoadingRoute) {
    t10 = isLoadingRoute && <RouteLoadingIndicator><Loading /></RouteLoadingIndicator>;
    $[16] = isLoadingRoute;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== previewMode) {
    t11 = previewMode && <PreviewWarning>PREVIEW MODE: Be aware, some content on this page might not yet be visible to clients.{" "}<a href="/api/preview?deactivate=1">Return to client mode</a></PreviewWarning>;
    $[18] = previewMode;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  let t13;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <ConfirmDetailsPopup />;
    t13 = <ToastNotificationContainer />;
    $[20] = t12;
    $[21] = t13;
  } else {
    t12 = $[20];
    t13 = $[21];
  }
  let t14;
  if ($[22] !== t10 || $[23] !== t11 || $[24] !== t9) {
    t14 = <>{t5}{t9}{t10}{t11}{t12}{t13}</>;
    $[22] = t10;
    $[23] = t11;
    $[24] = t9;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  return t14;
};
const MainContentWrap: FC<{
  pageComponent: ReactNode;
}> = t0 => {
  const $ = _c(12);
  const {
    pageComponent
  } = t0;
  const {
    isMenuOpen,
    closeMenu
  } = useNavigationContext();
  let t1;
  if ($[0] !== pageComponent) {
    t1 = <SubscribeButtonLockContextProvider>{pageComponent}</SubscribeButtonLockContextProvider>;
    $[0] = pageComponent;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== closeMenu || $[3] !== isMenuOpen || $[4] !== t1) {
    t2 = <MainContent id="main" role="main" $isMobileMenuOpen={isMenuOpen} onMouseEnter={closeMenu}>{t1}</MainContent>;
    $[2] = closeMenu;
    $[3] = isMenuOpen;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== closeMenu || $[7] !== isMenuOpen) {
    t3 = <StyledFooter $isMobileMenuOpen={isMenuOpen} onMouseEnter={closeMenu} />;
    $[6] = closeMenu;
    $[7] = isMenuOpen;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== t2 || $[10] !== t3) {
    t4 = <>{t2}{t3}</>;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  return t4;
};

// if the mobile menu is open hide the rest of the page, but only at screen sizes that would show the mobile menu
const MainContent = styled.main<{
  $isMobileMenuOpen: boolean;
}>`
    display: ${({
  $isMobileMenuOpen
}) => $isMobileMenuOpen ? 'none' : 'block'};
    ${breakpoints.L`display: block;`};
`;
const StyledFooter = styled(Footer)<{
  $isMobileMenuOpen: boolean;
}>`
    display: ${({
  $isMobileMenuOpen
}) => $isMobileMenuOpen ? 'none' : 'block'};
    ${breakpoints.L`display: block;`};
`;
export const PreviewWarning = styled.div`
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    background: red;
    color: white;
    font-weight: bold;
    padding: var(--size-grid-quarter);
    padding-inline: max(var(--size-grid-quarter), env(safe-area-inset-left, 0px), env(safe-area-inset-right, 0px));
    padding-bottom: max(var(--size-grid-quarter), env(safe-area-inset-bottom, 0px));
    text-align: center;
    z-index: var(--zIndex-overlayTop);
`;
const fadeIn = keyframes`
    0%, 75% {
        opacity: 0;
    }
    100% {
        opacity: 1;
    }
`;
const RouteLoadingIndicator = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    z-index: var(--zIndex-navigationBar);
    background: var(--color-body-imageOverlay);
    pointer-events: none;
    display: flex;
    align-items: center;
    justify-content: center;
    animation: ${fadeIn} 500ms linear 0s 1 normal forwards;
`;
const SkipLink = styled(Button)`
    position: absolute !important;
    height: 1px;
    width: 1px;
    overflow: hidden;
    clip: rect(1px, 1px, 1px, 1px);

    &:focus {
        position: absolute;
        height: auto;
        width: auto;
        overflow: hidden;
        clip: auto;
        top: var(--size-grid-half);
        left: var(--size-grid-half);
        z-index: var(--zIndex-skipLinks);
    }
`;
function _temp() {
  trackPageLoad();
  const url = new URL(window.location.href);
  if (url.searchParams.has(externalSourceQuery) || url.searchParams.has(fromLoginQuery)) {
    url.searchParams.delete(externalSourceQuery);
    url.searchParams.delete(fromLoginQuery);
    window.history.replaceState(window.history.state, "", url.toString());
  }
}
function _temp2(b) {
  b.removeAttribute("autoplay");
}
function _temp3() {
  if (window.matchMedia("(prefers-reduced-motion)").matches) {
    document.querySelectorAll("video[autoplay]").forEach(_temp2);
  }
}